import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { NAV } from "@/constants/testIds";
import { SITE } from "@/data/siteData";
import { Phone, Menu, X } from "lucide-react";

const links = [
    { id: "hero", label: "Home", testId: NAV.linkManifesto },
    { id: "menu", label: "Menu", testId: NAV.linkMenu },
    { id: "dove", label: "Dove Siamo", testId: NAV.linkDove },
    { id: "contatti", label: "Contatti", testId: NAV.linkContatti },
];

const Nav = () => {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 40);
        window.addEventListener("scroll", onScroll);
        onScroll();
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const scrollTo = (id) => {
        setOpen(false);
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    return (
        <motion.header
            initial={{ y: -60, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8, ease: [0.2, 0.9, 0.2, 1], delay: 0.4 }}
            className="fixed top-0 left-0 right-0 z-50"
        >
            <div
                className={`mx-3 md:mx-6 mt-3 md:mt-4 rounded-full transition-colors duration-300 ${
                    scrolled
                        ? "bg-[#0a0a0a] text-white"
                        : "bg-white/85 backdrop-blur-md text-[#0a0a0a]"
                } border-2 border-[#0a0a0a]`}
            >
                <div className="flex items-center justify-between px-4 md:px-6 py-2.5 md:py-3">
                    <button
                        data-testid={NAV.logo}
                        onClick={() => scrollTo("hero")}
                        className="flex items-center gap-2 group"
                    >
                        <span className="inline-flex items-center justify-center w-8 h-8 md:w-9 md:h-9 rounded-full bg-[#E63946] text-white font-display text-lg">
                            S
                        </span>
                        <span className="font-display text-xl md:text-2xl tracking-tight">
                            SENSO UNICO
                        </span>
                    </button>

                    <nav className="hidden md:flex items-center gap-8">
                        {links.map((l) => (
                            <button
                                key={l.id}
                                data-testid={l.testId}
                                onClick={() => scrollTo(l.id)}
                                className="nav-link text-sm uppercase tracking-[0.18em] font-semibold"
                            >
                                {l.label}
                            </button>
                        ))}
                    </nav>

                    <a
                        href={`tel:${SITE.phoneRaw}`}
                        data-testid={NAV.ctaCall}
                        className={`hidden md:inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold uppercase tracking-wider border-2 border-current ${
                            scrolled
                                ? "bg-white text-[#0a0a0a]"
                                : "bg-[#E63946] text-white border-[#0a0a0a]"
                        }`}
                    >
                        <Phone className="w-4 h-4" strokeWidth={2.5} />
                        Chiama
                    </a>

                    <button
                        className="md:hidden p-2"
                        onClick={() => setOpen((v) => !v)}
                        aria-label="menu"
                    >
                        {open ? <X /> : <Menu />}
                    </button>
                </div>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="md:hidden mx-3 mt-2 rounded-2xl border-2 border-[#0a0a0a] bg-white p-4"
                    >
                        <div className="flex flex-col gap-3">
                            {links.map((l) => (
                                <button
                                    key={l.id}
                                    onClick={() => scrollTo(l.id)}
                                    className="text-left font-display text-3xl"
                                >
                                    {l.label}
                                </button>
                            ))}
                            <a
                                href={`tel:${SITE.phoneRaw}`}
                                className="mt-2 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-full bg-[#E63946] text-white font-semibold border-2 border-[#0a0a0a]"
                            >
                                <Phone className="w-4 h-4" /> {SITE.phone}
                            </a>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.header>
    );
};

export default Nav;
